// Action Center — pure filter/sort/query logic, split out of the component
// so the tricky bits (the changes_requested own-exception, stale-key
// reconciliation, the empty-state variant, per-tab column reconciliation)
// are unit-testable without mounting a LitElement. Mirrors the rules in
// docs/action-center-plan.md Part 6.2/6.3, and — from the Part 6.5 ruling —
// the tab-strip regrouping: source selection is no longer a togglable filter,
// it's which tab you're on.

import type { ActionCenterSavedFilters, ActionCenterStatusCount, ActionItem } from "../types/actionCenter.js";

/** The known source/status vocabulary, learned from a response's `sources[]`. */
export interface ActionCenterVocab {
	sourceKeys: string[];
	statusSlugs: string[];
}

export const EMPTY_VOCAB: ActionCenterVocab = { sourceKeys: [], statusSlugs: [] };

export function vocabFromSources(
	sources: Array<{ key: string; statuses: ActionCenterStatusCount[] }>,
): ActionCenterVocab {
	const statusSlugs = new Set<string>();
	for (const s of sources) for (const st of s.statuses) statusSlugs.add(st.slug);
	return { sourceKeys: sources.map((s) => s.key), statusSlugs: [...statusSlugs] };
}

/**
 * The one source whose items carry a mediation group (Attend events —
 * legacy's "publisher → branch → district" chain). Hardcoded rather than
 * derived from the vocab because there is no generic per-source signal for
 * "supports mediation" in the wire contract; Reserve rooms (and any future
 * AC7 Appointments source) simply never populate `mediation_group_id`.
 * Gating the mediation SELECT — and, critically, the `mediation_groups=`
 * QUERY PARAM — on this constant is what keeps switching to the Rooms tab
 * from silently sending a filter that would zero out every room row (rooms
 * have no mediation_group_id to match against).
 */
export const MEDIATION_TAB_KEY = "attend-events";

/** Fully-resolved runtime filter state (every field concrete, no "undefined = default" left to interpret). Shared across tabs — see the module doc for why that's safe for `hiddenStatuses` (status slugs are disjoint per source) and a deliberate simplification for `locations`/`creators` (see the stage report). */
export interface ActionCenterFilterState {
	hiddenStatuses: string[];
	locations: number[];
	creators: number[];
	mediationGroupId: number | null;
	hidePast: boolean;
}

/**
 * "unknown/stale saved keys drop silently" (Part 6.2, Oakville's column
 * picker) — generalized to any saved key-list. Distinguishes a stale save
 * (every saved key has since vanished from the vocab → fall back to
 * "show all") from a genuinely intentional empty save (the user unchecked
 * everything → respect that).
 */
export function reconcileList<T extends string>(saved: T[] | undefined, known: readonly T[]): T[] {
	if (saved === undefined) return [...known];
	const kept = saved.filter((s) => (known as readonly string[]).includes(s));
	return kept.length > 0 || saved.length === 0 ? kept : [...known];
}

const CHANGES_REQUESTED = "changes_requested";

/**
 * Resolve the saved preference (which only carries the keys the user has
 * touched) against the live vocab + the caller's own mediation group.
 *
 * `changes_requested` is the one status with a "your own still shows"
 * exception (Chris/Kansas City/Cuyahoga, Part 6.2) — modelled as an ordinary
 * entry in `hiddenStatuses` whose OFF (default) state means "hidden for
 * everyone except the caller," not "hidden, full stop." See `isRowVisible`.
 * It only ever appears in the Events tab's own vocab, so the rule
 * automatically applies "ONLY on the events tab" (Part 6.5) with no
 * per-tab special-casing here.
 */
export function resolveFilters(
	saved: ActionCenterSavedFilters,
	vocab: ActionCenterVocab,
	callerGroupId: number | null,
): ActionCenterFilterState {
	const defaultHidden = vocab.statusSlugs.includes(CHANGES_REQUESTED) ? [CHANGES_REQUESTED] : [];
	return {
		hiddenStatuses: saved.hidden_statuses !== undefined ? reconcileList(saved.hidden_statuses, vocab.statusSlugs) : defaultHidden,
		locations: saved.locations ? [...saved.locations] : [],
		creators: saved.creators ? [...saved.creators] : [],
		// Absent key -> reassert the caller's own group every load (legacy
		// behaviour, Part 6.3). An explicit null (or a specific id) is a
		// real saved choice and wins.
		mediationGroupId: "mediation_group_id" in saved ? saved.mediation_group_id ?? null : callerGroupId,
		hidePast: saved.hide_past ?? true,
	};
}

/**
 * Table or Board (12 September 2026): a single global choice, persisted
 * in the same `action_center_filters` blob alongside `tab`. Unlike `tab`
 * there is no vocab to reconcile against: the value is either the literal
 * string "board" or it is treated as "table", covering both an absent key
 * (never saved) and a stale/unrecognised value.
 */
export type ActionCenterView = "table" | "board";

export function resolveView(saved: ActionCenterSavedFilters): ActionCenterView {
	return saved.view === "board" ? "board" : "table";
}

/**
 * Which tab is active — a SEPARATE concept from "filters" (it's the view
 * you're on, not a narrowing within it), but persisted in the same
 * `action_center_filters` blob per Part 6.5 ("the active tab persists in
 * action_center_filters"). Falls back to the first tab in server order
 * when nothing is saved, or the saved tab has since retired.
 */
export function resolveActiveTab(saved: ActionCenterSavedFilters, vocab: ActionCenterVocab): string {
	if (vocab.sourceKeys.length === 0) return "";
	if (saved.tab && vocab.sourceKeys.includes(saved.tab)) return saved.tab;
	return vocab.sourceKeys[0];
}

function sameSet(a: string[], b: string[]): boolean {
	if (a.length !== b.length) return false;
	const sb = new Set(b);
	return a.every((x) => sb.has(x));
}

/**
 * Has the user deviated from the fresh, computed defaults? Drives which
 * empty-state variant renders (Part 6.5) — a "you're caught up" screen for
 * the ordinary default view, "nothing matches these filters" once the user
 * has actually narrowed something. `hidePast=false` is deliberately excluded:
 * it only ever WIDENS the result set, so it can never itself explain a zero.
 * Which TAB is active is deliberately NOT part of this check — switching
 * tabs is navigation, not filtering, and an empty tab is either "caught up"
 * or "nothing yet" for that source, never "filtered empty" on its own.
 */
export function isNonDefaultFilter(
	state: ActionCenterFilterState,
	vocab: ActionCenterVocab,
	callerGroupId: number | null,
): boolean {
	const defaultHidden = vocab.statusSlugs.includes(CHANGES_REQUESTED) ? [CHANGES_REQUESTED] : [];
	if (!sameSet(state.hiddenStatuses, defaultHidden)) return true;
	if (state.locations.length > 0) return true;
	if (state.creators.length > 0) return true;
	if (state.mediationGroupId !== callerGroupId) return true;
	return false;
}

/**
 * The `statuses=` list to actually SEND to the server. `changes_requested`
 * always rides along even when its chip reads "hidden" — the own-exception
 * rule (below) needs the caller's own rows to have arrived in order to keep
 * them, and the server has no way to say "this status, but only mine."
 */
export function requestedStatuses(vocab: ActionCenterVocab, hiddenStatuses: string[]): string[] {
	const shown = vocab.statusSlugs.filter((s) => !hiddenStatuses.includes(s));
	if (vocab.statusSlugs.includes(CHANGES_REQUESTED) && !shown.includes(CHANGES_REQUESTED)) {
		shown.push(CHANGES_REQUESTED);
	}
	return shown;
}

/**
 * Row-level visibility, applied client-side after the fetch. Every status
 * other than `changes_requested` is a plain hidden/shown toggle; that one
 * status's "hidden" (default) state means "hidden — unless it's yours."
 */
export function isRowVisible(item: ActionItem, hiddenStatuses: string[]): boolean {
	const hidden = hiddenStatuses.includes(item.status);
	if (item.status !== CHANGES_REQUESTED) return !hidden;
	if (!hidden) return true; // the "Declined / changes requested" chip is revealed
	// Own rows always show: your declined draft is your to-do (COM-9077).
	// `is_creator` is SERVER-computed — creator_id is a legacy user id, so a
	// client-side comparison against the COS user id can never be right.
	return item.is_creator === true;
}

/**
 * The `?…` query object shared by the overview (all-sources counts) and
 * per-tab (items) requests — everything except `sources`/`mediation_groups`,
 * which are tab-specific and layered on by `buildTabQuery` below. Comma-
 * joined arrays, matching the rest of the app's list filters (Stream 4
 * precedent) rather than relying on `toQuery`'s bare String() on an array.
 */
export function buildOverviewQuery(
	state: ActionCenterFilterState,
	vocab: ActionCenterVocab,
): Record<string, string | number> {
	const params: Record<string, string | number> = { hide_past: state.hidePast ? 1 : 0 };
	if (vocab.statusSlugs.length > 0) {
		const req = requestedStatuses(vocab, state.hiddenStatuses);
		if (req.length < vocab.statusSlugs.length) params.statuses = req.join(",");
	}
	if (state.locations.length > 0) params.locations = state.locations.join(",");
	if (state.creators.length > 0) params.creators = state.creators.join(",");
	return params;
}

/**
 * The per-tab items query: the overview params PLUS `sources=<activeTab>`
 * (Part 6.5 — "the active tab IS the source filter"), plus `mediation_groups`
 * — but ONLY when the active tab is the one source that has a mediation
 * concept (see `MEDIATION_TAB_KEY`). Deliberately never sent on any other
 * tab, mediation default or not.
 */
export function buildTabQuery(
	state: ActionCenterFilterState,
	vocab: ActionCenterVocab,
	activeTab: string,
): Record<string, string | number> {
	const params = buildOverviewQuery(state, vocab);
	params.sources = activeTab;
	if (activeTab === MEDIATION_TAB_KEY && state.mediationGroupId != null) {
		params.mediation_groups = String(state.mediationGroupId);
	}
	return params;
}

// ---------------------------------------------------------------------------
// Column picker (Oakville, Part 6.2) — per-tab as of the tabs rework
// (Part 6.5 item 4: "the column PICKER persists per tab").
// ---------------------------------------------------------------------------

export const OPTIONAL_COLUMN_KEYS = ["status", "date", "last_update", "location", "creator", "mediation_level"] as const;
export type OptionalColumnKey = (typeof OPTIONAL_COLUMN_KEYS)[number];

export interface TabColumnConfig {
	/** Header label for the (always-shown) title column. */
	titleLabel: string;
	/** Header label for the optional "date" column. */
	dateLabel: string;
	/** Header label for the optional "creator" column. */
	creatorLabel: string;
	/** Header label for the optional "location" column. */
	locationLabel: string;
	/** Which optional columns this tab offers, in display order — also the picker's own option list and the "show all" default. */
	columnKeys: OptionalColumnKey[];
}

const CIRCULATE_FUNDS_COLUMNS: TabColumnConfig = {
	titleLabel: "Fund",
	// The projected date the fund runs out. Percent left and available ride
	// the title and its subtitle. No last-update column: Circulate states a
	// fund's condition, not when it began.
	dateLabel: "Projected empty",
	creatorLabel: "Creator",
	locationLabel: "Location",
	columnKeys: ["status", "date"],
};

// Legacy shape (Part 6.3's exact enumeration) for the two ports; a future
// registered source (AC7 Appointments, or anything else) gets sensible
// generic labels + a sensible default column set with zero code change here
// — see DEFAULT_TAB_COLUMNS below.
const TAB_COLUMN_CONFIG: Record<string, TabColumnConfig> = {
	"attend-events": {
		titleLabel: "Event",
		dateLabel: "Next date",
		creatorLabel: "Creator",
		locationLabel: "Location",
		columnKeys: ["status", "date", "last_update", "creator", "mediation_level"],
	},
	"reserve-rooms": {
		titleLabel: "Booking",
		dateLabel: "When",
		// The creator cell renders the STAFF creator (empty for patron
		// self-service bookings) and the location cell renders the BRANCH —
		// the old "Contact"/"Room" labels described neither (the contact
		// name is already the row title; the room rides the title's
		// subtitle). Honest words, 2026-08-19 demo-prep pass.
		creatorLabel: "Booked by",
		locationLabel: "Location",
		columnKeys: ["status", "date", "location", "creator"],
	},
	"circulate-funds": CIRCULATE_FUNDS_COLUMNS,
};

const DEFAULT_TAB_COLUMNS: TabColumnConfig = {
	titleLabel: "Title",
	dateLabel: "Date",
	creatorLabel: "Creator",
	locationLabel: "Location",
	columnKeys: ["status", "date", "last_update", "location", "creator"],
};

export function tabColumnConfig(sourceKey: string): TabColumnConfig {
	return TAB_COLUMN_CONFIG[sourceKey] ?? DEFAULT_TAB_COLUMNS;
}

/** The `action_center_columns` preference's per-tab shape. */
export type ActionCenterSavedColumnsPerTab = Record<string, string[]>;

/**
 * `action_center_columns` migrated from one flat array (pre-tabs, Part 6.2)
 * to a per-source map (Part 6.5 item 4). A legacy flat array predates tabs
 * entirely, so there's no source it was ever "for" — it's treated as the
 * EVENTS tab's saved set (Events was the primary/first-listed source in the
 * pre-tabs surface), and every other tab starts from its own computed
 * default. Never crashes on the old shape; an already-migrated map passes
 * through untouched.
 */
export function migrateColumnsPref(saved: string[] | ActionCenterSavedColumnsPerTab | undefined): ActionCenterSavedColumnsPerTab {
	if (saved === undefined) return {};
	if (Array.isArray(saved)) return { [MEDIATION_TAB_KEY]: saved };
	return saved;
}

export function reconcileColumnsForTab(perTab: ActionCenterSavedColumnsPerTab, sourceKey: string): OptionalColumnKey[] {
	const cfg = tabColumnConfig(sourceKey);
	return reconcileList(perTab[sourceKey] as OptionalColumnKey[] | undefined, cfg.columnKeys);
}

// ---------------------------------------------------------------------------
// Search (Part 6.5 item 3 — new, client-side only, no server param)
// ---------------------------------------------------------------------------

/** Case-insensitive substring match over the fields a person would actually recognise a row by. An empty/whitespace-only query matches everything. */
export function matchesSearch(item: ActionItem, query: string): boolean {
	const q = query.trim().toLowerCase();
	if (!q) return true;
	return [item.title, item.subtitle, item.creator_name, item.location_name].some(
		(v) => typeof v === "string" && v.toLowerCase().includes(q),
	);
}

// ---------------------------------------------------------------------------
// Sort (client-side, deliberately — see cos-action-center.ts for why)
// ---------------------------------------------------------------------------

export type ActionCenterSortKey = "title" | "status" | "date" | "last_update" | "location" | "creator" | "mediation_level";

function sortValue(item: ActionItem, key: string): string {
	switch (key) {
		case "title":
			return item.title ?? "";
		case "status":
			return item.status_label || item.status || "";
		case "date":
			return item.date ?? "";
		case "last_update":
			return item.raised_at ?? "";
		case "location":
			return item.location_name ?? "";
		case "creator":
			return item.creator_name ?? "";
		case "mediation_level":
			return item.mediation_group_name ?? "";
		default:
			return "";
	}
}

export function sortItems(items: ActionItem[], key: string, dir: "asc" | "desc"): ActionItem[] {
	const sign = dir === "desc" ? -1 : 1;
	return [...items].sort((a, b) => {
		const va = sortValue(a, key);
		const vb = sortValue(b, key);
		if (va < vb) return -1 * sign;
		if (va > vb) return 1 * sign;
		return 0;
	});
}

// ---------------------------------------------------------------------------
// Empty state (Part 6.5 — no week strip in v1; see the class doc for why).
// Now per-TAB (each tab is its own queue), so the caller passes the active
// tab's own hasHistory/filtered signals.
// ---------------------------------------------------------------------------

export type ActionCenterEmptyVariant = "filtered" | "nothing-yet" | "caught-up";

export function computeEmptyVariant(opts: { visibleCount: number; hasHistory: boolean; filtered: boolean }): ActionCenterEmptyVariant | null {
	if (opts.visibleCount > 0) return null;
	if (opts.filtered) return "filtered";
	return opts.hasHistory ? "caught-up" : "nothing-yet";
}

// ---------------------------------------------------------------------------
// Status chip tone (presentational only — the contract has no enum for
// this, so it's a heuristic over the slug rather than a fixed vocabulary).
// ---------------------------------------------------------------------------

export type StatusTone = "warning" | "danger" | "info" | "neutral";

export function statusTone(slug: string): StatusTone {
	if (/declin|cancel|reject/.test(slug)) return "danger";
	// circulate-funds: a fund under its threshold is the urgent one; a
	// forecast is a warning.
	if (slug === "low") return "danger";
	if (slug === "projected") return "warning";
	if (/draft|changes_requested|pending|payment_processing/.test(slug)) return "warning";
	// A booking approval waits on a person (warning); event review stays
	// the info blue. Teal is the brand colour, never a status.
	if (/approval/.test(slug)) return "warning";
	if (/awaiting|review/.test(slug)) return "info";
	return "neutral";
}

// ---------------------------------------------------------------------------
// Landing page (Part 5, O1 refinement — level 1 keeps the 3-way choice;
// partner-tier (level >= 3) gains action-center/dashboard, never inbox).
// ---------------------------------------------------------------------------

export function resolveLandingPath(user: { level: number; landing_page?: string } | null | undefined): string {
	if (!user) return "/dashboard";
	if (user.level === 1) {
		if (user.landing_page === "dashboard") return "/dashboard";
		if (user.landing_page === "action-center") return "/action-center";
		return "/inbox";
	}
	if (user.level >= 3) {
		return user.landing_page === "action-center" ? "/action-center" : "/dashboard";
	}
	return "/dashboard";
}
